import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ColumnOptionsStateKey } from '@shared-features/models/column-options-state-key.model';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { enumToArray } from '@shared/utils/enum-to-array.util';
import { EnumModel } from '@shared/model/enum-model';

@Component({
  selector: 'app-column-options-field-list',
  templateUrl: './column-options-field-list.component.html',
  styleUrls: ['./column-options-field-list.component.scss'],
})
export class ColumnOptionsFieldListComponent implements OnChanges {
  @Input() formGroup: FormGroup;
  @Input() keys: any[];
  @Input() state: ColumnOptionsStateKey[];
  /**
   * Columns this list marks mandatory: they cannot be removed, reordered, or swapped out.
   *
   * Defaults to empty, so every list that does not supply them behaves exactly as before.
   */
  @Input() lockedKeys: string[] = [];
  /**
   * Labels to render VERBATIM, keyed by column key.
   *
   * A column listed here skips `'Reports.' + suffix | translate` entirely. That is required for a
   * column whose name is data the user typed - a customer dynamic field - because there is no
   * translation key to look up, so the lookup would render the missing key itself.
   *
   * Absent for every pre-existing list, so they all stay fully translated.
   */
  @Input() rawLabels: Record<string, string> = {};
  @Output() stateUpdate = new EventEmitter();

  optionsPerColumn: { [order: number]: EnumModel[] };

  isLocked(key: string): boolean {
    return (this.lockedKeys || []).includes(key);
  }

  internalStateChange = false;

  get form(): FormArray {
    return this.formGroup.get('state') as FormArray;
  }

  draggedColumnOrder = -1;
  draggedOverColumnOrder = -1;

  ngOnChanges({ state }: SimpleChanges): void {
    if (state?.currentValue !== state?.previousValue) {
      if (!this.internalStateChange) {
        this.setStateOptions();
        this.initForm();
      } else {
        this.internalStateChange = false;
      }
    }
  }

  initForm(): void {
    this.formGroup.removeControl('state');
    this.formGroup.addControl('state', new FormArray([]));

    const state = [...(this.state || [])];

    state
      .sort((a, b) => a.order - b.order)
      .forEach((item) => {
        this.form.push(new FormControl(item.key, [Validators.required]));
      });

    this.form.valueChanges.subscribe((values) => {
      const newState = (values || []).map((key, order) => ({ key, order }));
      this.internalStateChange = true;
      this.stateUpdate.emit(newState);
    });
  }

  swapOrder(): void {
    if (this.draggedOverColumnOrder > -1 && this.draggedColumnOrder > -1) {
      const draggedKey = this.state[this.draggedColumnOrder]?.key;
      const draggedOverKey = this.state[this.draggedOverColumnOrder]?.key;

      // A swap moves BOTH keys, so it is refused when either end is locked - otherwise dragging an
      // unlocked column onto a locked one would displace the locked one.
      if (this.isLocked(draggedKey) || this.isLocked(draggedOverKey)) {
        this.draggedOverColumnOrder = -1;
        this.draggedColumnOrder = -1;
        return;
      }
      const newState = this.state.map((item) => {
        if (item.order === this.draggedColumnOrder) {
          return { ...item, key: draggedOverKey };
        } else if (item.order === this.draggedOverColumnOrder) {
          return { ...item, key: draggedKey };
        }
        return { ...item };
      });

      this.draggedOverColumnOrder = -1;
      this.draggedColumnOrder = -1;
      // this.setStateOptions();
      // this.initForm();
      this.internalStateChange = false;
      this.stateUpdate.emit(newState);
    }
  }

  removeColumn(key, order): void {
    if (this.isLocked(key)) {
      return;
    }

    const state = this.state
      .filter((item) => item.key !== key)
      .sort((a, b) => a.order - b.order)
      .map((item) => ({
        ...item,
        order: item.order < order ? item.order : item.order - 1,
      }));

    // this.setStateOptions();
    // this.initForm();
    this.internalStateChange = false;
    this.stateUpdate.emit(state);
  }

  addMoreColumns(): void {
    const control = new FormControl(null, [Validators.required]);
    this.optionsPerColumn[this.form.length] = enumToArray(this.keys).filter(
      (item) => !this.form.value.includes(item.key)
    );
    this.form.push(control);
    control.updateValueAndValidity({ onlySelf: false });
  }

  setStateOptions(): void {
    const state = this.state || [];
    this.optionsPerColumn = {};
    state.forEach((item) => {
      if (this.isLocked(item.key)) {
        // Only itself, so the dropdown cannot swap a locked column for another.
        //
        // Deliberately NOT `control.disable()`: a disabled control is excluded from
        // `FormArray.value`, so the locked key would vanish from the state this component emits.
        this.optionsPerColumn[item.order] = enumToArray(this.keys).filter(
          (keyItem) => keyItem.key === item.key
        );
        return;
      }

      const options = enumToArray(this.keys).filter((keyItem) => {
        const exist = state.find(
          (stateItem) => stateItem.key !== item.key && keyItem.key === stateItem.key
        );
        return !exist;
      });
      this.optionsPerColumn[item.order] = options;
    });
  }
}
